/**
 * Agent CRUD mutation hooks for the AC-1377 React Query migration
 * (AC-1407 / Phase 4.1).
 *
 * Implements the contract from the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`:
 *   - Section 2.6 - mutation + invalidation contract (agent rows)
 *   - Section 4   - Phase 4 project resources
 *   - Section 5.3 - mutation hook signatures
 *
 * ## Why this exists
 *
 * Agent CRUD currently happens via `useProjectStore.{createAgent,
 * updateAgent,deleteAgent}`, which call `apiService.*` directly and then
 * mutate the project-store `agents` and `allAgents` arrays in-place. Every
 * consumer (`AgentsPage`, `AgentFormModal`, `ToolManagementSystem`,
 * `Dashboard`) reads from the same Zustand slice; cross-component refresh
 * works only because the slice is module-level singleton state.
 *
 * That pattern is exactly the shape that produced AC-1390 / AC-1391 /
 * AC-1394 (stale-modal-data class) for workflows. These hooks centralize
 * the contract: every agent mutation knows it must invalidate
 * `[project, projectId, agents]`, and call sites get atomic
 * "edit agent -> list and modals both refresh" semantics. The
 * project-store consumers are NOT migrated by this ticket - that's
 * AC-1411. The store slices remain wired up during the transition; AC-1412
 * removes them once nothing reads from them. This ticket is intentionally
 * pessimistic per TD section 2.7 (CRUD writes are identity-affecting).
 *
 * ## Why projectId at hook level (closure), not mutate-time
 *
 * Agent CRUD is always scoped to the SAME project for the lifetime of the
 * surface that triggers it: `AgentsPage` is mounted inside a project's
 * route, the agent modal opens for an agent in that project,
 * `ToolManagementSystem` operates per-project. Closing `projectId` at
 * hook construction time gives us:
 *
 *   1. Cleaner call sites: `mutate({ data })` /
 *      `mutate({ agentId, data })` / `mutate({ agentId })` instead of
 *      threading `projectId` through every callsite.
 *   2. A single source of truth for which project's cache key to invalidate
 *      - the mutation cannot accidentally invalidate the wrong project.
 *   3. Symmetry with `useAgents(projectId)` and the step-mutation pattern in
 *      `useStepMutations.ts`.
 *
 * The TD's section-5.3 signatures
 * (`useUpdateAgent(projectId, agentId)` / `useDeleteAgent(projectId, agentId)`)
 * are summarized form; in practice agentId varies per `mutate()` call within
 * the same surface (e.g. clicking "Delete" on different rows of the agents
 * list), so the implementation takes only `projectId` at hook level. This
 * matches the orchestrator's instruction for AC-1407.
 *
 * ## Invalidation strategy
 *
 * On every agent mutation we invalidate `queryKeys.projectAgents(projectId)`.
 * That is the only consumer key for project-scoped agents. The cross-project
 * `allAgents` list (Dashboard) is NOT invalidated here - it has no
 * corresponding RQ key in this ticket; AC-1411 wires up `useAllAgents`
 * (or the equivalent) and that hook will own its own invalidation contract.
 *
 * Note that `queryKeys.projectModels(projectId)` is NOT invalidated by agent
 * CRUD - agent.output_model_id can reference a model row, but mutating an
 * agent does not change the model row itself. (Compare with
 * `useModelMutations.ts`, which invalidates `projectRegistries` because
 * registry-enabled state derives from model fields.)
 *
 * ## API
 *
 *   const create = useCreateAgent(projectId);
 *   await create.mutateAsync({ data: { name: 'Helper', instructions: '...' } });
 *
 *   const update = useUpdateAgent(projectId);
 *   await update.mutateAsync({ agentId: 42, data: { name: 'Renamed' } });
 *
 *   const remove = useDeleteAgent(projectId);
 *   await remove.mutateAsync({ agentId: 42 });
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import { Agent, CreateAgentForm } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Create-agent input. `data` is the form payload; `apiService.createAgent`
 * threads `project_id` server-side from the projectId captured in the hook
 * closure, so callers do not need to repeat it.
 */
export type CreateAgentVariables = {
  data: CreateAgentForm;
};

/**
 * Update-agent input. `agentId` is the canonical numeric id from the server
 * (`Agent.agent_id`); we accept `string | number` to match how callers
 * receive it (route params are strings, store ids are numbers). `data` is a
 * partial because in practice agent modals send only the fields they touched.
 */
export type UpdateAgentVariables = {
  agentId: string | number;
  data: Partial<CreateAgentForm>;
};

/**
 * Delete-agent input. `agentId` only - the projectId is in the closure.
 */
export type DeleteAgentVariables = {
  agentId: string | number;
};

/**
 * `useCreateAgent(projectId)` - mutation hook for
 * `POST /api/v1/projects/:projectId/agents`.
 *
 * On success, the freshly-created agent is appended to the
 * `projectAgents(projectId)` cache SYNCHRONOUSLY before `invalidateQueries`
 * fires. This is the AC-1447 fix: subscribers (e.g. `WorkflowEditor`'s
 * `useAgents`, which feeds `StepConfigurationModal`'s agent dropdown) see
 * the new agent on the next render without waiting for a network round-trip.
 * The subsequent invalidation reconciles with server truth in the background.
 *
 * @param projectId The project that will own the new agent. Captured in
 *                  closure so the mutation always invalidates the right
 *                  project's cache.
 *
 * @example
 *   const create = useCreateAgent(projectId);
 *   await create.mutateAsync({ data: form });
 */
export function useCreateAgent(
  projectId: string | number,
): UseMutationResult<Agent, Error, CreateAgentVariables> {
  const queryClient = useQueryClient();
  return useMutation<Agent, Error, CreateAgentVariables>({
    mutationFn: ({ data }) =>
      apiService.createAgent(String(projectId), data),
    onSuccess: (newAgent) => {
      // AC-1447: Synchronously append the new agent to the cache so any
      // subscriber re-renders with the new row before the background
      // refetch resolves. Without this, the dropdown that reads
      // `agents.find(a => a.agent_id === formData.agent_id)?.name` after
      // setting `formData.agent_id = newAgent.agent_id` falls back to
      // "Select an agent..." until the user closes and reopens the modal.
      const key = queryKeys.projectAgents(projectId);
      queryClient.setQueryData<Agent[]>(key, (prev) => {
        if (!prev) return [newAgent];
        // Guard against an unlikely-but-possible duplicate id - the server
        // is the source of truth, so if it returned an id we already have
        // we keep the latest copy.
        const filtered = prev.filter(
          (a) => a.agent_id !== newAgent.agent_id,
        );
        return [...filtered, newAgent];
      });
      queryClient.invalidateQueries({
        queryKey: key,
      });
    },
  });
}

/**
 * `useUpdateAgent(projectId)` - mutation hook for
 * `PUT /api/v1/agents/:agentId`.
 *
 * On success, the updated agent row replaces the existing entry in the
 * `projectAgents(projectId)` cache SYNCHRONOUSLY before `invalidateQueries`
 * fires (AC-1447). This propagates renames and other field edits to
 * subscribers without waiting for a refetch.
 *
 * @param projectId The project that owns the agent being updated. Captured
 *                  in closure so the mutation always invalidates the right
 *                  project's cache.
 *
 * @example
 *   const update = useUpdateAgent(projectId);
 *   await update.mutateAsync({ agentId: 42, data: { name: 'Renamed' } });
 */
export function useUpdateAgent(
  projectId: string | number,
): UseMutationResult<Agent, Error, UpdateAgentVariables> {
  const queryClient = useQueryClient();
  return useMutation<Agent, Error, UpdateAgentVariables>({
    mutationFn: ({ agentId, data }) =>
      apiService.updateAgent(String(agentId), data),
    onSuccess: (updatedAgent) => {
      // AC-1447: Synchronously replace the matching row in the cache so
      // edits propagate to any subscriber on the next render. Match by
      // `agent_id`. If no prior cache entry exists, leave it alone -
      // the invalidation below will populate it on the next subscribe.
      const key = queryKeys.projectAgents(projectId);
      queryClient.setQueryData<Agent[]>(key, (prev) => {
        if (!prev) return prev;
        return prev.map((a) =>
          a.agent_id === updatedAgent.agent_id ? updatedAgent : a,
        );
      });
      queryClient.invalidateQueries({
        queryKey: key,
      });
    },
  });
}

/**
 * `useDeleteAgent(projectId)` - mutation hook for
 * `DELETE /api/v1/agents/:agentId`.
 *
 * On success, the deleted agent is removed from the
 * `projectAgents(projectId)` cache SYNCHRONOUSLY before `invalidateQueries`
 * fires (AC-1447). This is the symmetric counterpart to the create-time
 * fix - without it, a just-deleted agent stays visible in any UI listening
 * to the cache until the background refetch resolves.
 *
 * @param projectId The project that owned the deleted agent. Captured in
 *                  closure so the mutation always invalidates the right
 *                  project's cache.
 *
 * @example
 *   const remove = useDeleteAgent(projectId);
 *   if (confirm('Delete?')) await remove.mutateAsync({ agentId: 42 });
 */
export function useDeleteAgent(
  projectId: string | number,
): UseMutationResult<void, Error, DeleteAgentVariables> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteAgentVariables>({
    mutationFn: ({ agentId }) =>
      apiService.deleteAgent(String(agentId)),
    onSuccess: (_void, { agentId }) => {
      // AC-1447: Synchronously drop the row from the cache. Use Number()
      // coercion to compare ids since callers may pass either string or
      // number (matching `DeleteAgentVariables`).
      const key = queryKeys.projectAgents(projectId);
      const targetId = Number(agentId);
      queryClient.setQueryData<Agent[]>(key, (prev) => {
        if (!prev) return prev;
        return prev.filter((a) => a.agent_id !== targetId);
      });
      queryClient.invalidateQueries({
        queryKey: key,
      });
    },
  });
}
